import {
  cachedColorForCategory,
  cachedLabelForCategory,
} from '../planning/categoryCache';
import { getDateLocale } from '../../i18n';

export const EVENT_CATEGORIES = ['HO', 'HNO', 'Astreinte', 'Congés', 'Formation', 'Absence'];

export const CATEGORY_LABELS = {
  HO: 'Heures Ouvrées',
  HNO: 'Heures Non Ouvrées',
  Astreinte: 'Astreinte',
  Congés: 'Congés',
  Formation: 'Formation',
  Absence: 'Absence',
};

/** Accent bar styles matching schedule_view.html */
export const CATEGORY_ACCENTS = {
  HO: { className: 'bg-nokia-blue', style: undefined },
  HNO: { className: 'bg-primary', style: undefined },
  Astreinte: { className: '', style: { backgroundColor: '#d97706' } },
  Congés: { className: 'bg-nokia-green', style: undefined },
  Formation: { className: '', style: { backgroundColor: '#ca8a04' } },
  Absence: { className: '', style: { backgroundColor: '#e11d48' } },
};

const COLOR_TOKEN_ACCENTS = {
  blue: { className: 'bg-nokia-blue', style: undefined },
  primary: { className: 'bg-primary', style: undefined },
  amber: { className: '', style: { backgroundColor: '#d97706' } },
  green: { className: 'bg-nokia-green', style: undefined },
  yellow: { className: '', style: { backgroundColor: '#ca8a04' } },
  rose: { className: '', style: { backgroundColor: '#e11d48' } },
};

function getWeekdayShort() {
  const monday = new Date(2024, 0, 1);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toLocaleDateString(getDateLocale(), { weekday: 'short' });
  });
}

export function toIsoDateLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseIsoDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addMonths(date, delta) {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

export function startOfMonth(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function formatMonthTitle(date) {
  return date.toLocaleDateString(getDateLocale(), { month: 'long', year: 'numeric' });
}

export function getCurrentScheduleLabel() {
  return formatMonthTitle(startOfMonth());
}

export function formatDateLabel(isoDate) {
  const date = parseIsoDate(isoDate);
  if (!date) return '';
  return date.toLocaleDateString(getDateLocale(), {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatWeekday(isoDate) {
  const date = parseIsoDate(isoDate);
  if (!date) return '';
  return date.toLocaleDateString(getDateLocale(), { weekday: 'long' });
}

export function formatShortDateLabel(isoDate) {
  const date = parseIsoDate(isoDate);
  if (!date) return '';
  return date.toLocaleDateString(getDateLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatFilterDateDisplay(isoDate) {
  const date = parseIsoDate(isoDate);
  if (!date) return '';
  return date.toLocaleDateString(getDateLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatWhenLabel(isoDate, start, end) {
  const date = parseIsoDate(isoDate);
  if (!date) return `${start} – ${end}`;
  const dayPart = date.toLocaleDateString(getDateLocale(), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  return `${dayPart} · ${start} – ${end}`;
}

export function labelForCategory(category) {
  return cachedLabelForCategory(category, CATEGORY_LABELS[category] || category);
}

export function accentForCategory(category) {
  if (CATEGORY_ACCENTS[category]) return CATEGORY_ACCENTS[category];
  const cached = cachedColorForCategory(category, null);
  if (cached) {
    // Prefer hex from API cache; map legacy tokens via COLOR_TOKEN_ACCENTS
    if (COLOR_TOKEN_ACCENTS[cached]) return COLOR_TOKEN_ACCENTS[cached];
    if (String(cached).startsWith('#')) {
      return { className: '', style: { backgroundColor: cached } };
    }
  }
  return CATEGORY_ACCENTS.HO;
}

export function timeToMinutes(value) {
  const [h, m] = String(value || '00:00').split(':').map(Number);
  return h * 60 + m;
}

export function minutesToTimeValue(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function getNowCreateSlot() {
  const now = new Date();
  let start = Math.floor((now.getHours() * 60 + now.getMinutes()) / 30) * 30;
  start = Math.max(0, Math.min(start, 23 * 60));
  let end = Math.min(start + 60, 24 * 60);
  if (end <= start) end = start + 30;
  return {
    start: minutesToTimeValue(start),
    end: minutesToTimeValue(end),
  };
}

/** Monday-start calendar cells for a month view inside the filter datepicker. */
export function buildDatepickerCells(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const first = new Date(year, month, 1);
  const dow = first.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  const gridStart = new Date(year, month, 1 + offset);
  const todayIso = toIsoDateLocal(new Date());
  const cells = [];

  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push({
      iso: toIsoDateLocal(d),
      dayNum: d.getDate(),
      outside: d.getMonth() !== month,
      today: toIsoDateLocal(d) === todayIso,
    });
  }

  return { weekdays: getWeekdayShort(), cells };
}

/** Seed events from schedule_view.html demo, remapped to the viewed month. */
export function buildSeedEventsForSchedule(monthDate = startOfMonth()) {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  const templates = [
    { day: 9, title: 'Morning Standup', start: '09:00', end: '10:00', category: 'HO' },
    { day: 9, title: 'Quick Sync', start: '11:30', end: '12:00', category: 'HNO' },
    { day: 10, title: 'Lunch with Team', start: '12:30', end: '13:30', category: 'Congés' },
    { day: 10, title: 'Deep Work', start: '14:00', end: '16:30', category: 'Astreinte' },
  ];

  return templates
    .filter((t) => t.day <= daysInMonth)
    .map((t, i) => ({
      id: `sched-seed-${y}-${m + 1}-${i}`,
      date: toIsoDateLocal(new Date(y, m, t.day)),
      title: t.title,
      start: t.start,
      end: t.end,
      category: t.category,
    }));
}
